import React from "react";

import { FormProvider, useForm } from "react-hook-form";

import { useConnection } from "src/api/connectApi/queries";
import { Connection } from "src/api/connectApi/types";
import { useWorkspace } from "src/api/queries";
import { FieldErrors } from "src/api/types";
import { LoadingView } from "src/base-components/LoadingView";
import { useDiffViewContext } from "src/changeHistory/DiffViewModal/DiffViewContext";
import {
  InboundWebhookConnectionNode,
  InboundWebhookConnectionNodeData,
} from "src/constants/NodeDataTypes";
import { Accordion } from "src/design-system/Accordion";
import { ConfigPane } from "src/inboundWebhookNode/ConfigPane";
import {
  RequestMapping,
  ResponseMapping,
} from "src/inboundWebhookNode/FieldMappings";
import { InboundWebhookConnectionForm } from "src/inboundWebhookNode/types";
import { NodeEditorBaseProps } from "src/nodeEditor/NodeEditor";
import { convertFieldErrorsBeToFe } from "src/utils/FieldErrorUtils";
import { useSubmitForm } from "src/utils/useSubmitForm";

type WrapperProps = {
  node: InboundWebhookConnectionNode;
  workspaceId: string;
} & NodeEditorBaseProps<InboundWebhookConnectionNodeData>;

export const InboundWebhookNodeEditor: React.FC<WrapperProps> = ({
  immutable,
  node,
  workspaceId,
  displayedError,
  isReactive,
  onUpdate,
}) => {
  const workspace = useWorkspace(workspaceId);
  const connection = useConnection({
    baseUrl: workspace.data?.base_url,
    id: node.data.connectionId,
  });
  return (
    <LoadingView
      queryResult={connection}
      renderUpdated={(connection: Connection) => (
        <InboundWebhookConnectionNodeEditor
          connection={connection}
          displayedError={displayedError}
          immutable={immutable}
          isReactive={isReactive}
          node={node}
          onUpdate={onUpdate}
        />
      )}
    />
  );
};

type Props = {
  connection: Connection;
  immutable: boolean;
  node: InboundWebhookConnectionNode;
} & NodeEditorBaseProps<InboundWebhookConnectionNodeData>;

const InboundWebhookConnectionNodeEditor: React.FC<Props> = ({
  connection,
  immutable,
  node,
  displayedError,
  isReactive,
  onUpdate,
}) => {
  const { renderedInDiffView } = useDiffViewContext();
  const currentFormValues: InboundWebhookConnectionForm = {
    correlation_id: node.data.correlation_id,
    response: node.data.response,
    config: node.data.config,
  };

  const formProps = useForm<InboundWebhookConnectionForm>({
    mode: "onChange",
    defaultValues: currentFormValues,
    ...(isReactive && { values: currentFormValues }),
  });

  useSubmitForm({
    onChange: (data: InboundWebhookConnectionForm) => {
      onUpdate({ newData: data });
    },
    disabled: isReactive,
    previousValues: currentFormValues,
    watch: formProps.watch,
  });

  const runFieldErrors: FieldErrors | undefined = displayedError?.field_errors
    ? convertFieldErrorsBeToFe(displayedError.field_errors)
    : undefined;

  return (
    <FormProvider {...formProps}>
      <form>
        <Accordion defaultValue={["request", "response", "config"]}>
          <Accordion.Item
            isReadOnly={renderedInDiffView}
            label="Configure request"
            value="request"
          >
            <RequestMapping
              immutable={immutable}
              runFieldErrors={runFieldErrors}
            />
          </Accordion.Item>
          <Accordion.Item
            isReadOnly={renderedInDiffView}
            label="Configure response"
            value="response"
          >
            <ResponseMapping
              immutable={immutable}
              runFieldErrors={runFieldErrors}
            />
          </Accordion.Item>
          <Accordion.Item
            isReadOnly={immutable || renderedInDiffView}
            label="Advanced settings"
            value="config"
          >
            <ConfigPane
              connection={connection}
              immutable={immutable}
              runFieldErrors={runFieldErrors}
            />
          </Accordion.Item>
        </Accordion>
      </form>
    </FormProvider>
  );
};
